import { Dir } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    Injector,
    input,
    model,
    OnDestroy,
    OnInit,
    output,
    runInInjectionContext,
    signal,
    viewChild,
    viewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatAccordion } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { selectTranslatedKey } from '@common/store/i18n/selectors/i18n.selectors';
import { StoryHttpActions } from '@common/store/story/actions/story.actions';
import { Prompt } from '@common/store/story/models/story.models';
import { selectIsLockedSuccessfully, selectIsLockingInProgress, selectPrompts, selectSelectedText, selectShouldRequireLock } from '@common/store/story/selector/story.selectors';
import { GridDragTypes } from '@common/utils/grid-utils';
import { isStoryApproveField } from '@common/utils/util';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { environment } from '@src/environments/environment';
import { AppService } from '@src/suite/app.service';
import { StTextAreaOverlayComponent } from '@src/suite/common/controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StoryIconListComponent } from '@src/suite/common/controls/story-icon-list/story-icon-list';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { ClipboardTypes } from '@src/suite/common/enums/clipboard-types';
import { GENERIC_STORY_OUTPUT_TYPE } from '@src/suite/common/enums/constants';
import { DragEventKeys, FieldDefValidations, StoryOutputType, ToolbarMenuCommandType, ToolbarMenuId } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StoryEditorTextService } from '@src/suite/common/services/story-editor-text.service';
import { StoryService } from '@src/suite/common/services/story-service';
import { StFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/full-screen/full-screen';
import {
    FullscreenActions,
    selectAllStoriesMap,
    selectCurrentVisibleSidecar,
    selectFullscreenCurrentViewRecord,
    selectIsFullscreenStoryLoose,
    selectLayoutTemplate,
    SidecarEfffectActions,
    SidecarMenu,
} from '@src/suite/common/store/fullscreen';
import { selectCollaborativeEditingAPIKey, selectEnforceRequiredFieldsOnCreate, selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectDirectionality } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectParentStoryByGuid, selectStoryTemplates, selectStoryTemplatesMap, selectSubStoriesByParentGuid } from '@src/suite/common/store/records/records.selectors';
import { formatDurationLikeMoment, toLuxonLocaleDateString, toLuxonRelativeDateString } from '@src/suite/common/utils/date-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { blackLineSpan, ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';
import { calculateRequiredFieldGroups, checkRequiredProperties, isStoryInRundown } from '@src/suite/common/utils/record-utils';
import { isLockedByUser } from '@src/suite/common/utils/story-utils';
import { getTimeSpanFromSeconds } from '@src/suite/common/utils/timing-format';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { v4 } from 'uuid';

import { LoaderComponent } from '../../../../loader/loader';
import { ExternalActionsOverlayComponent } from '../../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { AttachmentsListComponent } from '../../../components/attachments-list/attachments-list';
import { AvatarBigComponent } from '../../../components/avatar/avatar-big';
import { BetaComponent } from '../../../components/beta/st-beta.component';
import { StExpansionPanelComponent } from '../../../components/st-expansion-panel/st-expansion-panel.component';
import { ToolbarMenuComponent } from '../../../components/st-toolbar-menu/toolbar-menu/toolbar-menu.component';
import { DatePickerComponent } from '../../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../../controls/date-time-picker/date-time-picker';
import { DeskComponent } from '../../../controls/desk/desk';
import { StoryEditorComponent } from '../../../controls/st-story-editor/st-story-editor';
import { StTextAreaOverlayComponent as StTextAreaOverlayComponent_1 } from '../../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListComponent } from '../../../controls/staff-id-list/staff-id-list';
import { StatusComponent } from '../../../controls/status/status';
import { StoryOutputTypeComponent } from '../../../controls/story-output-type/story-output-type';
import { TagsAutocompleteComponent } from '../../../controls/tags/tags-autocomplete';
import { ClickStopPropagationDirective } from '../../../directives/stop-propagation';
import { StStoryFullScreenPropertiesComponent } from '../st-story-full-screen-properties/st-story-full-screen-properties';
import { MenuItem, storyActions } from './story-actions';

enum OverlayType {
    ExternalActions = 'ExternalActions',
    AllStoriesDropdown = 'AllStoriesDropdown',
}

@Component({
    selector: 'st-story-full-screen',
    imports: [
        AttachmentsListComponent,
        AvatarBigComponent,
        BetaComponent,
        CdkOverlayOrigin,
        ClickStopPropagationDirective,
        DatePickerComponent,
        DateTimePickerComponent,
        DeskComponent,
        Dir,
        ExternalActionsOverlayComponent,
        LoaderComponent,
        MatAccordion,
        MatButton,
        MatIcon,
        MatIconButton,
        MatMenuTrigger,
        MatProgressBar,
        MatTooltip,
        StaffIDListComponent,
        StatusComponent,
        StExpansionPanelComponent,
        StoryEditorComponent,
        StoryIconListComponent,
        StoryOutputTypeComponent,
        StStoryFullScreenPropertiesComponent,
        StTextAreaOverlayComponent_1,
        TagsAutocompleteComponent,
        ToolbarMenuComponent,
    ],
    templateUrl: './story-full-screen.html',
    styleUrl: './story-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-item-full-screen',
    },
})
export class StoryFullScreenComponent extends StFullScreenComponent implements OnInit, OnDestroy, AfterViewInit {
    // services
    injector = inject(Injector);
    basicContentService = inject(BasicContentService);
    storyService = inject(StoryService);
    nomWebServicesJson = inject(NomWebServicesJson);
    appService = inject(AppService);
    playbookService = inject(PlaybookService);
    private readonly storyEditorTextService = inject(StoryEditorTextService);

    StoryOutputType = StoryOutputType;
    OverlayType = OverlayType;
    pstFieldNames = PSTFieldNames;
    environment = environment;
    readonly FieldDefValidations = FieldDefValidations;

    // Inputs
    readonly loaderState = input<boolean>();
    readonly parentTopic = input<StorytellingTopicRecord | null>();
    override readonly data = model<StorytellingStoryRecord>();
    readonly isLoading = input<boolean>();
    readonly autoUpdate = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly updatingInProgress = input<boolean>();
    readonly standaloneStory = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    // computed properties
    readonly editorDisabled = computed(() => {
        return !this.isEditableByLock() || this.isDisabled() || this.isStoryDisabled();
    });

    readonly rundownTitleAndDate = computed(() => {
        return this.data()?.FullStorySlug?.match(/\[(.*?)\]/)?.[1] || `${this.data()?.ContainerTitle} ${toLuxonLocaleDateString(this.data()?.ContainerModTime)}`;
    });

    readonly isDragable = computed(() => {
        if (this.data()?.isNewRecord) return false;
        return !this.isStoryInRO() && !this.hasUnsavedChanges();
    });

    readonly items = computed(() => {
        const storyTemplates = this.storyTemplates();
        const prompts = this.prompts();
        const items: MenuItem[] = storyActions;

        const iterateOverItems = (localItems: MenuItem[]) => {
            for (const item of localItems) {
                if (item.id === ToolbarMenuId.STORY_GENERATE) {
                    item.disabled = this.standaloneStory();
                }
                if (item.id === ToolbarMenuId.PROPERTIES && this.enforceRequiredFieldsOnCreate() && !this.requiredFieldGroups()) {
                    item.isRequired = true;
                } else {
                    item.isRequired = false;
                }
                if (item.options && item.options.length > 0) {
                    item.label = item.labelKey ? this.languageSettings()[item.labelKey] : item.label || '';
                    iterateOverItems(item.options);
                } else {
                    if (item.id === ToolbarMenuId.STORY_NEW) {
                        item.disabled = !!this.data()?.isNewRecord;
                        item.options = storyTemplates.map((template: StorytellingRecord) => {
                            return {
                                id: ToolbarMenuId.STORY_NEW_TEMPLATE,
                                label: template.Title,
                                type: 'action',
                                commandType: ToolbarMenuCommandType.STORY,
                                metadata: {
                                    Guid: template.Guid,
                                    Type: template.Type,
                                    // TODO: BIG TODO - speak with Yanko about the chance of having multiple programs when creating from template see 'createStoryFromTemplate$ = createEffect(() => {' in playbook.effects
                                    Path: template.Path,
                                },
                            };
                        });
                    } else if (item.id === ToolbarMenuId.STORY_GENERATE) {
                        item.options = prompts.map((prompt: Prompt) => {
                            return {
                                id: ToolbarMenuId.STORY_GENERATE_PROMPT,
                                label: prompt.title,
                                type: 'action',
                                commandType: ToolbarMenuCommandType.STORY,
                                metadata: prompt,
                            };
                        });
                    } else {
                        item.label = item.labelKey ? this.languageSettings()[item.labelKey] : item.label || '';
                        if (item.id === ToolbarMenuId.STORY_SAVE || item.id === ToolbarMenuId.STORY_SAVE_AS_TEMPLATE) {
                            item.disabled = this.data()?.isNewRecord || !this.hasUnsavedChanges();
                        }
                    }
                }
            }
        };
        iterateOverItems(items);
        return items;
    });

    readonly timeUpdatedLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedAgoLabel))()} ${toLuxonRelativeDateString(this.data()?.ModTime, this.appService.updatedAgoSignal())}`;
    });

    readonly selectCurrentStoryModBy = computed(() => {
        return `${this.languageSettings().PSTWebChangedByLabel} ${this.globalTablesService.Staff[this.data()?.ModBy]?.DisplayName || this.data()?.ModBy}`;
    });
    readonly selectCurrentStoryReadTime = computed(() => {
        return formatDurationLikeMoment(this.data()?.TextTime);
    });
    readonly selectCurrentStoryActualTime = computed(() => {
        if (this.data()?.Actual) {
            const actualSeconds = this.data()?.Actual?.as('seconds') ?? 0;
            return getTimeSpanFromSeconds(actualSeconds);
        } else {
            return formatDurationLikeMoment(this.data()?.readTimeInternal);
        }
    });
    readonly storyTrTActualTextTime = computed(() => {
        return `${this.languageSettings().PSTWebTextTime}/${this.languageSettings().PSTWebActualTime}${this.selectCurrentStoryReadTime()}/${this.selectCurrentStoryActualTime()}`;
    });
    readonly selectCurrentStoryWordsCount = computed(() => {
        const latestContent = this.latestEditorContent();
        const text = latestContent ? (latestContent.aboveBlackLineText || '') + blackLineSpan + (latestContent.belowBlackLineText || '') : this.data()?.text;
        const parsedStoryText = parseXml(text, 'text/html');
        let count = 0;
        if (parsedStoryText.body) {
            const bodyChildren = parsedStoryText.body.children;
            for (const child of bodyChildren) {
                if (child?.tagName.toUpperCase() !== 'DIV') {
                    count += child?.textContent
                        ?.replace(/,|;|:/g, ' ')
                        ?.replace(/\s\s+/g, ' ')
                        ?.split(/\s+/)
                        ?.map(val => val.trim())
                        ?.filter(val => !!val).length;
                }
            }
        }
        return `${count || 0} ${this.languageSettings().PSTWebWordsCount}`;
    });
    readonly selectedText = computed(() => {
        return this.store.selectSignal(selectSelectedText(this.data()?.Guid))();
    });
    readonly selectCurrentStorySelectedReadRateTime = computed(() => {
        if (this.selectedText()?.TextTime > 0) {
            return getTimeSpanFromSeconds(this.selectedText()?.TextTime);
        }
        return null;
    });

    readonly codoxApiKey = computed(() => {
        return this.store.selectSignal(selectCollaborativeEditingAPIKey)();
    });

    readonly isStoryInRO = computed(() => {
        return isStoryInRundown(this.data());
    });

    readonly collaborationEnabled = computed(() => {
        // if story is part of a rundown we check if the story is locked, otherwise we don't care
        return this.data().isWriteable && this.codoxApiKey() && !this.data()?.isNewRecord && !this.isStoryInRO();
    });

    /**
     * !IMPORTANT NOTE ON isEditableByLock LOGIC
     *
     * Carefully change this logic, because it determines when the editor is editable in non-collaboration mode,
     * and any change can cause the editor to lose cursor position on every content change.
     */
    readonly isEditableByLock = computed(() => {
        if (this.collaborationEnabled()) {
            return true;
        }
        return (
            !this.data()?.EditLocked ||
            !this.data()?.EditLockedBy ||
            (this.data()?.EditLockedBy === this.nomWebServicesJson.UserId && (this.isLockedSuccessfully() == null || this.isLockedSuccessfully()))
        );
    });
    readonly lockLabel = computed(() => {
        if (this.lockingInProgress()) {
            return this.languageSettings().PSTWebLockingInProgress;
            // 'Locking...';
        } else if (this.isLockedSuccessfully()) {
            return this.languageSettings().PSTWebEditing;
        } else if (!this.isEditableByLock()) {
            return `${this.languageSettings().PSTWebLockedByOtherUser} ${this.globalTablesService.getUserNameByIDV2(this.data()?.EditLockedBy)}`;
        } else if (this.isEditableByLock()) {
            return this.languageSettings().PSTWebOpenToEdit;
        } else {
            return '';
        }
    });
    readonly isStoryOutputTypeDisabled = computed(() => {
        // is story has text it will be disabled
        return !!this.data()
            ?.text?.trim()
            ?.replace(/<[^>]*>?/gm, '');
    });

    readonly isRequiredFieldsPopulated = computed(() => {
        return checkRequiredProperties(this.data(), this.playbookService);
    });

    readonly approvedVisible = computed(() => isStoryApproveField(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.Approved]));

    readonly isDisabled = computed(() => {
        return !this.data()?.isWriteable || this.data()?.Path?.isWasteBinPath || this.data().topicMetadata?.Path?.isWasteBinPath || this.data()?.LegalBlock || !!this.data()?.isDisabled;
    });

    readonly requiredFieldGroups = computed(() => {
        const groups = calculateRequiredFieldGroups(this.data(), this.playbookService);
        for (const group in groups) {
            if (!groups[group]) {
                return false;
            }
        }
        return true;
    });

    readonly disabledByLoaderState = computed(() => {
        // TODO: check this
        // return !this.collaborationEnabled() && this.loaderState();
        return false;
    });
    readonly isStoryDisabled = computed(() => {
        return (this.disabledByLoaderState() && !this.data().isNewRecord) || (this.data()?.Locked && !this.isStoryLockedByMe() && this.data().LockedBy !== undefined) || !this.data().isWriteable;
    });

    readonly isStoryLockedByMe = computed(() => {
        return this.data()?.LockedBy === this.globalTablesService?.nomWebServicesJson?.HelloResults?.UserID;
    });

    readonly isLockedSuccessfully = computed(() => this.store.selectSignal(selectIsLockedSuccessfully(this.sidecarMenu()))());
    readonly lockingInProgress = computed(() => this.store.selectSignal(selectIsLockingInProgress(this.sidecarMenu()))());
    readonly shouldRequireLock = computed(() => this.store.selectSignal(selectShouldRequireLock(this.sidecarMenu()))());
    readonly dir = computed(() => {
        return this.store.selectSignal(selectDirectionality)();
    });

    readonly showAttachments = computed(() => {
        return environment?.demoFlag;
    });

    // signals
    readonly toggleEditor = signal(true);
    // True while Codox is connecting for the current story; drives the progress bar.
    readonly isCodoxInitializing = signal(false);
    // Tracks the latest content emitted by st-story-editor via (contentChanged).
    // Serves as both the content cache for auto-save and the source of truth for hasUnsavedChanges.
    // Reset to null in two places:
    //   - After the old story is released on story change (ngOnInit effect).
    //   - After a successful save (savePending cleared, explicit .set(null)).
    // Rationale for local cache over store/viewChild: the store update is async and view children
    // are destroyed before ngOnDestroy fires, so both are unreliable in the auto-save path.
    //
    // Plain signal (not linkedSignal): a linkedSignal that resets on data().Guid would be
    // processed before the ngOnInit effect, making the value null by the time the effect
    // reads it to save the old story on story switch.
    private readonly latestEditorContent = signal<{ aboveBlackLineText: string; belowBlackLineText: string } | null>(null);
    // Derived directly from latestEditorContent: true while there is tracked content that
    // has not yet been cleared by a successful save. No manual set(true/false) needed.
    readonly hasUnsavedChanges = computed(() => this.latestEditorContent() != null);
    override readonly expandedGroup = signal<string>('STORYFULLSCREEN');
    readonly collabUsers = signal<{ id: string }[]>([]);

    // store selector signals
    prompts = this.store.selectSignal(selectPrompts);
    scriptStoryTypeLabel$ = this.store.select(selectTranslatedKey(I18nKeys.PSTWebListValuePSTStoryOutputTypeGeneric));
    isStoryLoose = this.store.selectSignal(selectIsFullscreenStoryLoose);
    storyTemplates = this.store.selectSignal(selectStoryTemplates);
    allStoriesMap = this.store.selectSignal(selectAllStoriesMap);
    enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);
    readonly fieldDefDict = this.store.selectSignal(selectFieldDefsDict)();

    readonly addSubStoryLabelKey = I18nKeys.PSTWebAddSubStoryLabel;

    readonly isSubStory = computed(() => !!this.data()?.PSTPrimaryStoryGUID);

    readonly parentSubstoriesVisible = computed(() => {
        return !!this.data()?.visibilityCriteria?.[PSTFieldNames.PSTSubstoriesSequence] && !!this.data()?.[PSTFieldNames.PSTStoryTopicGUID];
    });

    readonly isNewTopic = computed(() => this.parentTopic()?.isNewRecord);

    readonly subStories = computed(() => {
        const storyGuid = this.data()?.Guid;
        if (!storyGuid) return [];
        if (this.isNewTopic()) {
            return (this.parentTopic().stories || []).filter(s => (s as StorytellingStoryRecord).PSTPrimaryStoryGUID === storyGuid) as StorytellingStoryRecord[];
        }
        return this.store.selectSignal(selectSubStoriesByParentGuid)()?.get(storyGuid) || [];
    });

    readonly parentStory = computed(() => {
        const parentGuid = this.data()?.PSTPrimaryStoryGUID;
        if (!parentGuid) return null;
        if (this.isNewTopic()) {
            return (this.parentTopic().stories || []).find(s => (s as StorytellingStoryRecord).Guid === this.data()?.PSTPrimaryStoryGUID) as StorytellingStoryRecord;
        }
        return this.store.selectSignal(selectParentStoryByGuid(parentGuid))();
    });

    readonly parentStoryList = computed(() => {
        const parent = this.parentStory();
        return parent ? [parent] : [];
    });

    // outputs
    readonly mosMediaClicked = output<string>();
    readonly promptSelected = output<Prompt>();
    readonly backToTopic = output<void>();

    // view child
    readonly richTextEditor = viewChild(StoryEditorComponent);
    readonly menuItems = viewChildren(MatMenuTrigger);
    readonly title = viewChild<StTextAreaOverlayComponent>('title');
    readonly storyFullScreen = viewChild<ElementRef>('storyFullScreen');

    //props
    currentStoryGuid = '';
    savePending = false;
    id = '';
    isExternalActionPanelOpen = false;
    isRTL = this.globalTablesService.getRtl();
    hasSelectedText = false;

    openRelatedStory(story: StorytellingStoryRecord) {
        // Story is rendered inside a sidecar panel — navigate within sidecar
        if (this.sidecarMenu()) {
            this.store.dispatch(
                SidecarEfffectActions.openStoryInSidecar({
                    guid: story.Guid,
                    sidecarType: this.sidecarMenu(),
                    sidecarMenu: SidecarMenu.STORIES,
                })
            );
            return;
        }

        // Fullscreen is already open — navigate within it
        this.store.dispatch(
            FullscreenActions.tryOpenStory({
                recordGuid: story.Guid,
                recordType: story.Type,
                standaloneStory: false,
            })
        );
    }

    openSubStoriesInSidecar() {
        // Resolve sidecar type: explicit sidecar menu > currently visible sidecar
        let sidecarType = this.sidecarMenu() || this.store.selectSignal(selectCurrentVisibleSidecar)();

        // Fullscreen has no sidecar visible (ONLY_MAIN layout) — force sidecar open by switching to BASIC layout
        if (!sidecarType) {
            const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
            if (currentTemplate === FullscreenLayoutTemplate.ONLY_MAIN) {
                sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                this.store.dispatch(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
            }
        }

        // Sidecar resolved — open the sub-stories list in it
        if (sidecarType) {
            const parentGuid = this.data()?.Guid;
            this.store.dispatch(SidecarEfffectActions.openSubStoriesInSidecar({ sidecarType, parentGuid }));
        }
    }

    createSubStory(event: { templateProgramId: string; templateGuids?: string[] }) {
        this.store.dispatch(
            PlaybookApiActions.createStoryFromTemplate({
                topic: this.parentTopic(),
                templateProgramId: event.templateProgramId,
                templateGuids: event.templateGuids,
                parentStoryGuid: this.data().Guid,
            })
        );
    }

    // lifecycle hooks
    ngOnInit() {
        this.id = v4();
        this.store.dispatch(StoryHttpActions.loadPrompts());
        this.store.dispatch(PlaybookApiActions.loadStoryTemplates());
        runInInjectionContext(this.injector, () => {
            effect(() => {
                if (this.data()?.Guid && this.data().Guid !== this.currentStoryGuid) {
                    const oldStory = this.allStoriesMap()?.get(this.currentStoryGuid);
                    if (oldStory) {
                        // Apply any unsaved editor content to the old story before releasing it,
                        // so keystrokes typed since the last auto-save are not lost on story switch.
                        const editorContent = this.latestEditorContent();
                        const storyToRelease = editorContent
                            ? { ...oldStory, text: (editorContent.aboveBlackLineText || '') + blackLineSpan + (editorContent.belowBlackLineText || '') }
                            : { ...oldStory };
                        this.releaseStory(storyToRelease);
                        this.latestEditorContent.set(null);
                    }
                    if (this.shouldRequireLock()) {
                        this.lockStory();
                    }
                    // Keep StoryEditorTextService in sync with the story now active in this
                    // editor slot so that MOS ncsStoryRequest responses always carry the most
                    // recent unsaved text.  The old Guid is removed first so a sidecar
                    // switching between stories never exposes stale text under the previous Guid.
                    if (this.currentStoryGuid) {
                        this.storyEditorTextService.unregisterTextGetter(this.currentStoryGuid);
                    }
                    this.currentStoryGuid = this.data().Guid;

                    if (this.currentStoryGuid != null) {
                        // Getter priority (see StoryEditorTextService for rationale):
                        //  1. richTextEditor().getCurrentContent() — live Froala DOM, most current
                        //     (predates the collaboration editor's 300 ms debounce).
                        //  2. latestEditorContent()               — last (contentChanged) emission;
                        //     fallback while the editor ViewChild is not yet in the DOM.
                        //  3. data()?.text                        — last explicitly saved NgRx value.
                        this.storyEditorTextService.registerTextGetter(this.currentStoryGuid, () => {
                            const editorContent = this.richTextEditor()?.getCurrentContent();
                            if (editorContent) {
                                return `${editorContent.aboveBlackLineText || ''}${blackLineSpan}${editorContent.belowBlackLineText || ''}`;
                            }
                            const content = this.latestEditorContent();
                            if (content) {
                                return `${content.aboveBlackLineText || ''}${blackLineSpan}${content.belowBlackLineText || ''}`;
                            }
                            return this.data()?.text ?? '';
                        });
                    }
                }
                if (!this.isLoading() && this.savePending) {
                    this.latestEditorContent.set(null); // clears hasUnsavedChanges
                    this.savePending = false;
                }
            });
        });
    }

    ngAfterViewInit() {
        if (this.data()?.isNewRecord) {
            setTimeout(() => {
                this.title()?.onContainerClick();
            }, 500);
        }
    }

    ngOnDestroy() {
        // Remove the text getter so PlaybookEffects does not call a closure that
        // references a destroyed component instance on a subsequent MOS request.
        this.releaseCurrentStory();
    }

    @HostListener('window:beforeunload')
    handleBeforeUnload() {
        this.releaseCurrentStory();
    }

    //TODO: Think about better way to handle this.
    // @HostListener('click')
    // handleClick() {
    //     if (this.data()?.Locked && !this.isStoryLockedByMe() && this.data().LockedBy != null) {
    //         this.store.dispatch(LiveApiActions.rundownStoriesLockedByAnotherUser({ lockedBy: this.data().LockedBy, lockTime: this.data().LockTime }));
    //     }
    // }

    releaseStory(story: StorytellingStoryRecord) {
        if (story?.Guid) {
            if (story.isNewRecord) {
                return;
            }
            if (!this.isDisabled()) {
                this.saveStory(story);
            }
            this.releaseStoryLock(story.Guid);
        } else {
            this.latestEditorContent.set(null);
        }
    }

    releaseCurrentStory() {
        // Unregistered first so that an exception from releaseStoryLock() or saveCurrentStory() cannot
        // leave a dangling closure in StoryEditorTextService.
        if (this.currentStoryGuid) {
            this.storyEditorTextService.unregisterTextGetter(this.currentStoryGuid);
        }
        if (this.data()?.isNewRecord) {
            return;
        }

        const lockedByMe = !this.data().EditLocked || isLockedByUser(this.data(), this.nomWebServicesJson.UserId);
        if (!this.isDisabled() && lockedByMe && this.hasUnsavedChanges()) {
            this.saveCurrentStory();
        }
        this.releaseStoryLock(this.data().Guid);
    }

    releaseStoryLock(storyGuid: string) {
        this.store.dispatch(StoryHttpActions.releaseStoryLock({ storyGuid: storyGuid, editorGuid: this.id }));
    }

    // Called from ngOnDestroy and window:beforeunload (via releaseCurrentStory).
    // Uses latestEditorContent — populated on every contentChanged event — so that
    // unsaved keystrokes are not lost even though the editor DOM is already torn down.
    // Falls back to this.data().text if the user never typed (latestEditorContent is null).
    saveCurrentStory() {
        const recordToSave = { ...this.data() };
        const editorContent = this.latestEditorContent();
        if (editorContent) {
            recordToSave.text = (editorContent.aboveBlackLineText || '') + blackLineSpan + (editorContent.belowBlackLineText || '');
        }
        const currentStory = this.allStoriesMap()?.get(recordToSave?.Guid);
        const lockState = currentStory?.EditLocked && currentStory?.EditLockedBy === this.nomWebServicesJson?.UserId;
        if (currentStory && !this.isDisabled() && (this.collaborationEnabled() || lockState)) {
            this.saveStory(recordToSave);
        }
    }

    saveStory(story: StorytellingStoryRecord) {
        if (!this.isDisabled()) {
            const fieldsToUpdate: EnpsField[] = [
                { key: 'Text', value: story.text },
                { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
            ];
            if (story.TextCommands) {
                fieldsToUpdate.push({ key: PSTFieldNames.TextCommands, value: story.TextCommands });
            }
            this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: story, fieldsToUpdate }));
            this.savePending = true;
        }
    }

    // component methods
    updateCurrentStory(fieldsToUpdate: EnpsField[]) {
        if (this.isDisabled()) {
            return;
        }
        const fieldDef: EnpsField[] = [];
        const titleField = fieldsToUpdate.find(field => field.key === PSTFieldNames.Title);
        const isUpdatingTitle = !!titleField;

        if (isUpdatingTitle && !titleField.value) {
            return;
        }

        fieldsToUpdate.forEach(field => {
            fieldDef.push(field);
            // if the story is new in the new topic and the text is updated, we should update the draft text as well
            if (this.data()?.isNewRecord && field.key === PSTFieldNames.PSTRichText) {
                fieldDef.push({ key: PSTFieldNames.PSTDraftText, value: field.value });
            }
        });

        const textUpdated = fieldsToUpdate.find(field => field.key === PSTFieldNames.PSTRichText);
        if (textUpdated && !this.autoUpdate()) {
            const newStory = { ...this.data() };
            fieldsToUpdate.forEach(field => {
                newStory[field.key] = field.value;
            });
            this.store.dispatch(StoriesRecordActions.updateStories({ records: [newStory] }));
            this.store.dispatch(StoriesRecordActions.calculateReadRateTime({ record: newStory, hasSelectedText: false }));
        } else {
            if (!this.isDisabled()) {
                this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef, isUpdatingTitle: isUpdatingTitle }));
            }
        }
    }

    // Receives every contentChanged event from st-story-editor (both basic and collab modes).
    // Only stores the value locally — no store dispatch. The store is updated only on explicit
    // save (onEditorSave), keeping change-detection churn to a minimum.
    onEditorContentChanged(content: { aboveBlackLineText: string; belowBlackLineText: string }) {
        this.latestEditorContent.set(content);
        const newStory = { ...this.data() };
        newStory.text = (content.aboveBlackLineText || '') + blackLineSpan + (content.belowBlackLineText || '');
        if (this.data()?.isNewRecord) {
            /**
             * when we have a new story we want to update the text in the store on every change,
             * because we want to make sure that the text will be saved when you click on one of the 'save' buttons.
             *
             * TODO: need to set save button as disabled when creating new story
             */
            this.updateCurrentStory([{ key: PSTFieldNames.PSTRichText, value: newStory.text }]);
        } else {
            this.store.dispatch(StoriesRecordActions.calculateReadRateTime({ record: newStory, hasSelectedText: false }));
        }
    }

    // Triggered by the editor's save event (toolbar button / Ctrl+S).
    // Also syncs latestEditorContent here to cover the edge case where the user saves
    // before the collab editor's 300 ms contentChanged debounce has fired.
    onEditorSave({ aboveBlackLineText, belowBlackLineText }: { aboveBlackLineText: string; belowBlackLineText: string }) {
        this.latestEditorContent.set({ aboveBlackLineText, belowBlackLineText });
        const combinedText = (aboveBlackLineText || '') + blackLineSpan + (belowBlackLineText || '');
        this.updateCurrentStory([{ key: PSTFieldNames.PSTRichText, value: combinedText }]);
        this.saveStory({ ...this.data(), text: combinedText });
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(StoriesRecordActions.updateStories({ records: [this.data()] }));
    }

    createNewStoryVersion(prompt: Prompt) {
        if (!this.standaloneStory()) {
            this.promptSelected.emit(prompt);
        }
    }

    onDragStart(event: Event, story: StorytellingStoryRecord | undefined) {
        const dragEvent = event as DragEvent;
        dragEvent.dataTransfer!.setData(
            DragEventKeys.DRAG_STORY,
            JSON.stringify({
                scriptDatabase: story.Database,
                scriptPath: story.Path?.fullPath,
                scriptGuid: story.Guid,
                actionType: GridDragTypes.ADD.toString(),
                text: story.text,
                textCommands: story.TextCommands,
            })
        );
        dragEvent.dataTransfer!.effectAllowed = 'copyMove';
    }

    overlayOutsideClick(event: MouseEvent, overlayType: OverlayType, buttonSelector: string) {
        const toggleBtn = (event?.target as HTMLElement).closest(buttonSelector);
        if (!toggleBtn) {
            switch (overlayType) {
                case OverlayType.ExternalActions:
                    this.isExternalActionPanelOpen = false;
                    break;
            }
        }
    }

    onMosItemOpen(event: MosEditorEvent) {
        // TODO: remove this when we have a better way to handle this
        // like we extract it from record and use objId to compare
        const mosCommand = this.data()?.TextCommands?.[event.mosId];
        if (mosCommand) {
            /**
             * Will try to open the mos item in the sidecar, but if the plugin is initiated,
             * we will have to open the side car and the mos plugin in it.
             * Than we wait for the mos plugin to initiate, and then open the mos item.
             */
            this.store.dispatch(
                SidecarEfffectActions.openMosItem({
                    mosPluginId: event.mosPluginId,
                    mosItemCommand: mosCommand,
                    mosItemId: event.mosId,
                    storyId: this.data().Guid,
                    layout: event.layout,
                    sidecar: event.sidecar,
                })
            );
        }
    }

    onMosMediaClicked(mosId: string) {
        this.mosMediaClicked.emit(mosId);
    }

    onApprovedChanged(approved: boolean) {
        this.updateCurrentStory([{ key: PSTFieldNames.Approved, value: approved }]);
    }

    onFocus() {
        // lock the story
        if (this.data()?.isNewRecord) {
            return;
        }
        this.lockStory();
    }
    lockStory() {
        this.store.dispatch(StoryHttpActions.lockStory({ storyGuid: this.data().Guid, editorGuid: this.id }));
    }

    onSelectedTextChanged(event: string) {
        const foundCommand = this.productionCommandFromSelection();
        const newStory = { ...this.data() };
        this.store.dispatch(StoriesRecordActions.calculateReadRateTime({ record: newStory, hasSelectedText: true, productionCommand: foundCommand, selectedText: event }));
    }

    onActionHandler(item: MenuItem) {
        switch (item?.commandType) {
            case ToolbarMenuCommandType.STORY:
                this.onStoryAction(item);
                break;
            case ToolbarMenuCommandType.EDITOR:
                this.onEditorAction(item);
                break;
            default:
                console.warn('unknown command type');
                break;
        }
        this.closeAllMenus();
    }

    closeAllMenus() {
        if (this.menuItems()?.length > 0) {
            for (const item of this.menuItems()) {
                if (item.openMenu) {
                    item.closeMenu();
                }
            }
        }
    }

    onStoryAction(item: MenuItem) {
        switch (item.id) {
            case ToolbarMenuId.STORY_NEW_TEMPLATE: {
                const metadata = item.metadata as Pick<StorytellingRecord, 'Guid' | 'Type' | 'Path'>;
                if (metadata?.Guid) {
                    //if the current story is in topic we add a new story to the topic and open it
                    if (this.data()?.PSTStoryTopicGUID) {
                        this.store.dispatch(
                            PlaybookApiActions.createStoryFromTemplate({ topic: this.parentTopic(), templateProgramId: metadata.Path?.fullPath, templateGuids: [metadata?.Guid], openStory: true })
                        );
                    }
                    //if the current story is in rundown we add a new story to the rundown
                    else if (this.data()?.Path?.isInCollection && !this.data()?.PSTStoryTopicGUID) {
                        const fullScreenRecord = this.store.selectSignal(selectFullscreenCurrentViewRecord)();
                        const template = this.store.selectSignal(selectStoryTemplatesMap)()?.get(metadata.Guid);
                        const fieldsToUpdate: Record<string, any> = {
                            [PSTFieldNames.Title]: template.Title,
                            [PSTFieldNames.Type]: ObjectTypes.SCRIPT,
                            [PSTFieldNames.PSTStoryOutputType]: template.PSTStoryOutputType ? template.PSTStoryOutputType : GENERIC_STORY_OUTPUT_TYPE,
                        };

                        this.store.dispatch(
                            LiveApiActions.insertRundownRow({
                                database: fullScreenRecord?.Database,
                                collectionPath: fullScreenRecord?.Type === ObjectTypes.RUNDOWN ? fullScreenRecord?.Path.fullPath : fullScreenRecord?.Path?.parentCollectionPath?.fullPath,
                                collectionGuid: fullScreenRecord?.Type === ObjectTypes.RUNDOWN ? fullScreenRecord?.Guid : fullScreenRecord?.Path?.parentCollectionGuid,
                                insertBeforeGuid: '0',
                                fieldsToUpdate,
                            })
                        );
                    }
                    // if the current story is loose we create a new story to tne New Items
                    else {
                        const template = this.store.selectSignal(selectStoryTemplatesMap)()?.get(metadata.Guid);
                        this.store.dispatch(PlaybookApiActions.createStory({ story: template, createAndNext: false, keepFullscreenModal: true }));
                    }
                }
                return;
            }
            case ToolbarMenuId.STORY_GENERATE_PROMPT: {
                const metadata = item.metadata as Prompt;
                if (metadata) {
                    this.store.dispatch(StoryHttpActions.generateNewVersion({ prompt: metadata, topic: this.parentTopic(), openNewPrompt: true }));
                }
                return;
            }
            case ToolbarMenuId.STORY_DUPLICATE: {
                this.store.dispatch(RecordHttpActions.duplicateAssignment({ record: this.data(), linkToParent: true }));
                break;
            }
            case ToolbarMenuId.STORY_COPY_TO_CLIPBOARD: {
                this.appService.clipboard.copy(this.data(), ClipboardTypes.Story);
                break;
            }
            case ToolbarMenuId.STORY_COPY_TO_STORY_LINK: {
                this.appService.clipboard.copy(this.data(), ClipboardTypes.StoryUrl);
                break;
            }
            case ToolbarMenuId.STORY_SAVE: {
                this.saveCurrentStory();
                break;
            }
            case ToolbarMenuId.STORY_SAVE_AS_TEMPLATE: {
                this.store.dispatch(RecordHttpActions.openNewTemplateDialog({ record: this.data(), isFullscreen: true }));
                break;
            }
            case ToolbarMenuId.STORY_RENAME: {
                this.title().onContainerClick();
                break;
            }
            // case ToolbarMenuId.STORY_MOVE:
            case ToolbarMenuId.DELETE: {
                this.store.dispatch(RecordHttpActions.deleteRecord({ record: this.data() }));
                break;
            }
            case ToolbarMenuId.PROPERTIES: {
                this.toggleEditorTogle();
                break;
            }
        }
    }

    onEditorAction(item: MenuItem) {
        if (!this.richTextEditor()?.currentEditorInstance()) {
            return;
        }
        switch (item.id) {
            case ToolbarMenuId.STORY_UNDO:
                this.richTextEditor()?.currentEditorInstance()?.commands.undo();
                break;
            case ToolbarMenuId.STORY_REDO:
                this.richTextEditor()?.currentEditorInstance()?.commands.redo();
                break;
            case ToolbarMenuId.STORY_CUT: {
                const selectedText = this.richTextEditor()?.currentEditorInstance()?.selection.text();
                navigator.clipboard.writeText(selectedText);
                this.richTextEditor()?.currentEditorInstance()?.html.insert('', true);
                break;
            }
            case ToolbarMenuId.STORY_COPY:
                navigator.clipboard.writeText(this.richTextEditor()?.currentEditorInstance()?.selection.text());
                break;
            case ToolbarMenuId.STORY_PASTE: {
                //Find a way to access the clipboard
                // const latestCopiedText = await navigator.clipboard.readText();
                // this below work and replace the selected text
                //this.richTextEditor().currentEditorInstance()?.html.insert('latestCopiedText', true);
                break;
            }
            case ToolbarMenuId.STORY_SELECT_ALL:
                this.richTextEditor()?.currentEditorInstance()?.commands.selectAll();
                break;
            case ToolbarMenuId.STORY_REMOVE: {
                this.richTextEditor().currentEditorInstance()?.html.insert('', true);
                break;
            }
            case ToolbarMenuId.EXPAND_ALL_ELEMENTS:
                this.richTextEditor()?.expandAllElements();
                break;
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.INSERT:
            case ProductionCommandType.DURATION:
            case ProductionCommandType.NOTES:
            case ProductionCommandType.TAKE:
            case ProductionCommandType.READRATE:
                // this.richTextEditor().insertProductionCommand(item.id);
                this.richTextEditor()?.currentEditorInstance()?.events.trigger('productionCommand', [item.id]);
                break;
            case ToolbarMenuId.BOLD:
                this.richTextEditor()?.currentEditorInstance()?.commands.bold();
                break;
            case ToolbarMenuId.ITALIC:
                this.richTextEditor()?.currentEditorInstance()?.commands.italic();
                break;
            case ToolbarMenuId.UNDERLINE:
                this.richTextEditor()?.currentEditorInstance()?.commands.underline();
                break;
            case ToolbarMenuId.STRIKETHROUGH:
                this.richTextEditor()?.currentEditorInstance()?.commands.strikeThrough();
                break;
            case ToolbarMenuId.SUBSCRIPT:
                this.richTextEditor()?.currentEditorInstance()?.commands.subscript();
                break;
            case ToolbarMenuId.SUPERSCRIPT:
                this.richTextEditor()?.currentEditorInstance()?.commands.superscript();
                break;
            //case ToolbarMenuId.FONT_SIZE_INCREASE:
            //NOT GONNA MAKE IT FOR NOW!
            // this.richTextEditor().currentEditorInstance()?.fontSize.apply('8px');
            //break;

            //PARAGRAPH STYLE COMMANDS HERE:
            case ToolbarMenuId.HEADLINE1:
            case ToolbarMenuId.HEADLINE2:
            case ToolbarMenuId.HEADLINE3:
                this.richTextEditor()?.currentEditorInstance()?.paragraphFormat.apply(item.id);
                break;
            case ToolbarMenuId.CLEAR_FORMATTING:
                this.richTextEditor().currentEditorInstance()?.commands.clearFormatting();
                break;

            //ALIGNMENT and INDENT COMMANDS HERE: THESE COMMANDS WORK BUT BACKEND DOESN'T SAVE THEM!!!
            // case ToolbarMenuId.ALIGN_LEFT:
            // case ToolbarMenuId.ALIGN_CENTER:
            // case ToolbarMenuId.ALIGN_RIGHT:
            // case ToolbarMenuId.ALIGN_JUSTIFY:
            //     this.richTextEditor().currentEditorInstance()?.align.apply(item.id);
            //     break;
        }
    }

    productionCommandFromSelection() {
        let foundCommand: string;

        if (this.data()?.TextCommands) {
            // Get the selection element from the editor
            const selection = this.richTextEditor().textSelection();
            if (selection.rangeCount !== 0) {
                const range = selection?.getRangeAt(0);
                const startElement = range?.startContainer;
                let currentNode = startElement;

                // Walk upwards through the DOM until we find a production command
                while (currentNode && !foundCommand) {
                    // Check if this node contains a production command
                    if (currentNode instanceof Element) {
                        const commandId = currentNode.getAttribute('id');
                        const prodCommand = this.data()?.TextCommands?.[commandId];
                        if (commandId && prodCommand && prodCommand.startsWith('[READRATE')) {
                            foundCommand = prodCommand;
                            break;
                        }
                    }
                    currentNode = currentNode.previousSibling || currentNode.parentNode;
                }
            }
        }
        return foundCommand;
    }

    onCollabUsersChanged(event: { id: string }[]) {
        this.collabUsers.set(event);
    }

    toggleEditorTogle() {
        this.storyFullScreen()?.nativeElement?.focus();
        this.toggleEditor.update(value => !value);
    }

    @HostListener('keydown.control.shift.p', ['$event'])
    onCtrlShiftP(event: Event) {
        this.toggleEditorTogle();
        event.preventDefault();
        event.stopPropagation();
        return false;
    }

    @HostListener('window:keydown.control.shift.p', ['$event'])
    preventCtrlShiftP(event: Event) {
        event.preventDefault();
        return false;
    }
}
